import { intl, isEmptyString, showError } from 'ehs-react';
import type { at } from '../generated/Beans';
import type { icModel, infrastructurePortalUtil } from 'generated-ts-common';
import { useEffect, useState } from 'react';
type CodedError = infrastructurePortalUtil.saf.infrastructure.model.common.CodedError;
type PatientBean = icModel.saf.infrastructure.model.virtualvisit.PatientBean;
type PatientData = at.sense.portlets.saf.virtualvisitpatient.model.PatientData;

export function displayError(error: CodedError): void {
  if (error.errorCode === 'VVPP0007') {
    showError(
      intl(
        'virtualvisitpatientportlet.region.institutions.blocked.message.content'
      )
    );
  } else {
    showError(error);
  }
}

export function getCorrectNameForChatAuthor(patientData?: PatientData): string {
  if (!patientData) {
    return intl('common.unknown');
  }
  const givenName = patientData.givenName;
  const familyName = patientData.familyName;
  if (!isEmptyString(givenName, false) && !isEmptyString(familyName, false)) {
    return givenName + ' ' + familyName;
  }

  if (!isEmptyString(givenName, false) && isEmptyString(familyName, false)) {
    return givenName;
  }

  if (isEmptyString(givenName, false) && !isEmptyString(familyName, false)) {
    return familyName;
  }
  return intl('common.unknown');
}

export interface ListA11yInputProps<T> {
  label: string;
  listContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  elementList: T[];
  selectElement: (element: T) => void;
  selectedElement: T | null;
  getElementId: (element: T) => string;
}
export interface ListA11yOutputProps<T> {
  activeElement: T | null;
}

export function useListA11y<T>(
  props: ListA11yInputProps<T>
): ListA11yOutputProps<T> {
  const [activeElement, setActiveElement] = useState<T | null>(null);
  useEffect(() => /*NOSONAR: cognitive complexity -> it's the easiest this hook could get*/ {
    const onRegionKeyDown = (e: KeyboardEvent) => {
      console.log('onRegionKeyPress', e.key);
      if (!props.elementList) return;
      if (e.key === 'ArrowUp') {
        if (!activeElement) {
          setActiveElement(props.elementList[0]);
        } else {
          const idx = props.elementList.indexOf(activeElement);
          if (idx > 0) {
            setActiveElement(props.elementList[idx - 1]);
          }
        }
      } else if (e.key === 'ArrowDown') {
        if (!activeElement) {
          setActiveElement(props.elementList[0]);
        } else {
          const idx = props.elementList.indexOf(activeElement);
          if (idx < props.elementList.length - 1) {
            setActiveElement(props.elementList[idx + 1]);
          }
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (activeElement) {
          props.selectElement(activeElement);
        }
      }
    };

    const onRegionFocus = () => {
      console.log('onRegionFocus');
      if (props.elementList && props.elementList.length > 0) {
        setActiveElement(props.elementList[0]);
      }
    };

    const onRegionFocusOut = () => {
      console.log('onRegionFocusOut');
      setActiveElement(null);
    };

    const myDiv = props.listContainerRef.current;
    if (myDiv) {
      myDiv.tabIndex = 0;
      myDiv.setAttribute('role', 'listbox');
      myDiv.setAttribute('aria-label', props.label);
      myDiv.setAttribute(
        'aria-activedescendant',
        activeElement ? props.getElementId(activeElement) : ''
      );

      myDiv.addEventListener('keydown', onRegionKeyDown);
      myDiv.addEventListener('focus', onRegionFocus);
      myDiv.addEventListener('focusout', onRegionFocusOut);
    }
    return () => {
      myDiv?.removeEventListener('keydown', onRegionKeyDown);
      myDiv?.removeEventListener('focus', onRegionFocus);
      myDiv?.removeEventListener('focusout', onRegionFocusOut);
    };
  }, [
    props.listContainerRef,
    props.elementList,
    activeElement,
    props.selectElement,
    props.selectedElement,
    props.label,
    props
  ]);
  return { activeElement };
}
export function mapPatientData(
  patientData: PatientData | undefined
): PatientBean {
  const patientBean = {
    firstName: patientData?.givenName,
    lastName: patientData?.familyName
  };
  return <PatientBean>patientBean;
}
